import { omit } from "lodash";
import { api } from "services";

import { ERROR_STATUS_CODE_403, IS_DEV, IS_ENG } from "globals/constants";
import { shell as constants } from "store/constants";
import { actionLoaderFactory } from "store/factories/fetchable";
import { dispatch } from "store/store";
import {
	defaultAttachmentTypes,
	defaultResolutions,
	defaultModificationTypes,
	defaultSubawardCostCap,
	defaultCategories,
	mapFormColumnHeaders,
	slugifyNonEmptyData,
	valueifyNonEmptyData,
	notifyError,
} from "utils";
import history from "utils/history";

import { setTenantData } from "./tenant";

export const setCayuseHeader = payload => dispatch({ type: constants.SET_CAYUSE_HEADER, payload });

const shellFactory = actionLoaderFactory(constants, constants.SHELL);

export const setShellData = shellFactory.setData;

const onSuccessFetchShell = (shellData, { getState }) => {
	shellFactory.setData(omit(shellData, "tenant"));

	const tenant = {
		...shellData.tenant,
		formColumnHeaders: mapFormColumnHeaders(shellData.tenant.formColumnHeaders, getState),
		resolutions: {
			PROPOSAL: valueifyNonEmptyData(shellData.tenant?.resolutions.PROPOSAL, defaultResolutions.PROPOSAL),
			AWARD: valueifyNonEmptyData(shellData.tenant?.resolutions.AWARD, defaultResolutions.AWARD),
			SUBAWARD: valueifyNonEmptyData(shellData.tenant?.resolutions.SUBAWARD, defaultResolutions.SUBAWARD),
			PROJECT: valueifyNonEmptyData(shellData.tenant?.resolutions.PROJECT, []),
		},
		categories: {
			PROPOSAL: slugifyNonEmptyData(shellData.tenant?.categories.PROPOSAL, defaultCategories.PROPOSAL)
		},
		subawardCostCap: !isNaN(shellData.tenant?.subawardCostCap)
			? shellData.tenant?.subawardCostCap
			: defaultSubawardCostCap,
		attachmentTypes: {
			PROPOSAL: valueifyNonEmptyData(shellData.tenant?.attachmentTypes, defaultAttachmentTypes),
		},
		modificationTypes: {
			modifications: valueifyNonEmptyData(shellData.tenant?.modificationTypes, defaultModificationTypes),
		},
		subawardModificationTypes: {
			modifications: valueifyNonEmptyData(shellData.tenant?.subawardModificationTypes, defaultModificationTypes),
		},
	};

	setTenantData(tenant);
};

const onErrorFetchShell = async (error, { getState }) => {
	if (error.response && error.response.status === ERROR_STATUS_CODE_403) {
		const response = await error.response.text();
		if (response) {
			const tenant = JSON.parse(response);

			window.location.replace("https://" + tenant.hostname);

			throw error;
		}

		history.push("/");
		history.go();

		throw error;
	} else {
		const message = IS_DEV ? error.message : getState().dictionary.common.shell.failedLoadingData;

		notifyError(message, error);
		shellFactory.setError(message);
	}
};

export const fetchShell = async () => {
	const result = await Promise.allSettled([
		api.tenantSettings.getFundingProgramLabel({ notifyUponError: false }),

		shellFactory.createLoaderAction(
			api.shell.get,
			[
				onSuccessFetchShell,
				onErrorFetchShell,
			],
		),
	]);

	if (result[0].status === "fulfilled") {
		setTenantData({ useFundingSchemeDictionary: /scheme/i.test(result[0].value.value) });
	}
};
